import React from 'react'
import queryString from 'query-string'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreHorizontal } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/features/shadcn/ui/select'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectTabPagination,
  selectCurrentTab,
  selectTrafficReport,
  fetchTrafficReport,
  selectTabTotalPages,
} from '@/applications/Traffic/trafficReportSlice'
import { DEFAULT_TIME_PERIOD } from '../constants'
import { getFiltersFromQs } from '../utils'

// Map tab names to their stats keys for accurate total counts
const TAB_TO_STATS_KEY = {
  orders: 'total_orders',
  checkouts: 'total_checkouts',
  carts: 'total_carts',
  visitors: 'total_visitors',
  sessions: 'total_sessions',
}

// Generate page numbers with ellipsis for large page counts
const getPageNumbers = (currentPage, totalPages) => {
  const pages = []
  const showEllipsisThreshold = 7

  if (totalPages <= showEllipsisThreshold) {
    // Show all pages if total is small
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
  } else {
    // Always show first page
    pages.push(1)

    if (currentPage > 3) {
      pages.push('ellipsis-start')
    }

    // Show pages around current page
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)

    for (let i = start; i <= end; i++) {
      if (!pages.includes(i)) {
        pages.push(i)
      }
    }

    if (currentPage < totalPages - 2) {
      pages.push('ellipsis-end')
    }

    // Always show last page
    if (!pages.includes(totalPages)) {
      pages.push(totalPages)
    }
  }

  return pages
}

export default function DataTablePagination({ table, showSelectedRowsLabel }) {
  const dispatch = useDispatch()
  const qs = queryString.parse(location.search)
  const timePeriod = qs.timePeriod || DEFAULT_TIME_PERIOD
  const customDateRange = qs.customDateRange ? JSON.parse(qs.customDateRange) : undefined
  const filters = getFiltersFromQs(qs)

  // Redux selectors
  const currentTab = useSelector(selectCurrentTab)
  const trafficReport = useSelector(selectTrafficReport)
  const tabPagination = useSelector((state) => selectTabPagination(state, currentTab))
  const tabTotalPages = useSelector(selectTabTotalPages)

  // Pagination state
  const pageIndex = tabPagination?.pageIndex || 1
  const pageSize = tabPagination?.pageSize || 20
  const totalPages = (currentTab && tabTotalPages[currentTab]) || 0

  // Get actual total count from stats
  const totalCount = trafficReport?.stats?.[TAB_TO_STATS_KEY[currentTab]] || 0

  // Calculate display values
  const startRecord = totalCount > 0 ? (pageIndex - 1) * pageSize + 1 : 0
  const endRecord = Math.min(pageIndex * pageSize, totalCount)
  const canGoPrevious = pageIndex > 1
  const canGoNext = pageIndex < totalPages

  const goToPage = (newPageIndex) => {
    dispatch(
      fetchTrafficReport({
        timePeriod,
        customDateRange,
        pagination: { pageIndex: newPageIndex, pageSize },
        tabName: currentTab,
        filters,
      })
    )
  }

  const handlePageSizeChange = (newPageSize) => {
    dispatch(
      fetchTrafficReport({
        timePeriod,
        customDateRange,
        pagination: { pageIndex: 1, pageSize: Number(newPageSize) },
        tabName: currentTab,
        filters,
      })
    )
  }

  return (
    <div className="pagination-wrapper tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-px-1 tw-py-3">
      {/* Rows per page */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-text-sm tw-text-slate-500 tw-whitespace-nowrap">Rows per page</span>
        <Select value={`${pageSize}`} onValueChange={handlePageSizeChange}>
          <SelectTrigger className="tw-h-8 tw-w-[70px]">
            <SelectValue placeholder={pageSize} />
          </SelectTrigger>
          <SelectContent side="top">
            {[10, 20, 30, 40, 50, 100, 250, 500, 1000].map((size) => (
              <SelectItem key={size} value={`${size}`}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Navigation */}
      <div className="tw-flex tw-items-center tw-gap-1">
        <button
          onClick={() => goToPage(1)}
          disabled={!canGoPrevious}
          className="tw-hidden sm:tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-text-slate-500 tw-transition-colors hover:tw-bg-slate-50 hover:tw-text-slate-700 disabled:tw-opacity-40 disabled:tw-pointer-events-none"
          title="First page"
        >
          <ChevronsLeft className="tw-h-4 tw-w-4" />
        </button>
        <button
          onClick={() => goToPage(pageIndex - 1)}
          disabled={!canGoPrevious}
          className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-text-slate-500 tw-transition-colors hover:tw-bg-slate-50 hover:tw-text-slate-700 disabled:tw-opacity-40 disabled:tw-pointer-events-none"
          title="Previous page"
        >
          <ChevronLeft className="tw-h-4 tw-w-4" />
        </button>

        {/* Page numbers */}
        {getPageNumbers(pageIndex, totalPages).map((pg) => {
          if (pg === 'ellipsis-start' || pg === 'ellipsis-end') {
            return (
              <span
                key={pg}
                className="tw-hidden sm:tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-text-slate-400"
              >
                <MoreHorizontal className="tw-h-4 tw-w-4" />
              </span>
            )
          }
          return (
            <button
              key={pg}
              onClick={() => goToPage(pg as number)}
              className={`tw-hidden sm:tw-inline-flex tw-h-8 tw-min-w-[2rem] tw-items-center tw-justify-center tw-rounded-md tw-px-2 tw-text-sm tw-font-medium tw-transition-colors ${
                pg === pageIndex
                  ? 'tw-bg-blue-600 tw-text-white tw-border tw-border-blue-600 tw-shadow-sm'
                  : 'tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 hover:tw-text-slate-900'
              }`}
            >
              {pg}
            </button>
          )
        })}

        {/* Mobile page indicator */}
        <span className="sm:tw-hidden tw-text-sm tw-font-medium tw-text-slate-600 tw-px-2 tw-whitespace-nowrap">
          {pageIndex} / {totalPages || 1}
        </span>

        <button
          onClick={() => goToPage(pageIndex + 1)}
          disabled={!canGoNext}
          className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-text-slate-500 tw-transition-colors hover:tw-bg-slate-50 hover:tw-text-slate-700 disabled:tw-opacity-40 disabled:tw-pointer-events-none"
          title="Next page"
        >
          <ChevronRight className="tw-h-4 tw-w-4" />
        </button>
        <button
          onClick={() => goToPage(totalPages)}
          disabled={!canGoNext}
          className="tw-hidden sm:tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-text-slate-500 tw-transition-colors hover:tw-bg-slate-50 hover:tw-text-slate-700 disabled:tw-opacity-40 disabled:tw-pointer-events-none"
          title="Last page"
        >
          <ChevronsRight className="tw-h-4 tw-w-4" />
        </button>
      </div>
    </div>
  )
}
